import { useCallback, useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus, X } from "lucide-react";
import { api, formatR, ORDER_STATUS_LABELS } from "@/lib/api";

const PERIODS = [
  { id: "daily", label: "Today" },
  { id: "weekly", label: "This week" },
  { id: "monthly", label: "This month" },
  { id: "annually", label: "This year" },
];

const METRICS = [
  { key: "orders", label: "Orders", metric: "all", money: false },
  { key: "gross_value", label: "Gross order value", metric: "all", money: true },
  { key: "paid_orders", label: "Paid orders", metric: "paid", money: false },
  { key: "paid_sales", label: "Paid sales", metric: "paid", money: true },
  { key: "pending_orders", label: "Pending payment", metric: "pending", money: false },
  { key: "cancelled_orders", label: "Cancelled", metric: "cancelled", money: false },
  { key: "refunded_orders", label: "Refunded orders", metric: "refunded", money: false },
  { key: "refund_amount", label: "Refund amount", metric: "refunded", money: true },
  { key: "net_sales", label: "Net sales", metric: "net", money: true, hero: true },
];

function Delta({ d, money }) {
  if (!d) return null;
  const fmt = (v) => (money ? formatR(Math.abs(v)) : Math.abs(v));
  const up = d.change > 0, flat = d.change === 0;
  return (
    <p className="text-[11px] mt-1.5 flex items-center gap-1 text-slate-500">
      {flat ? <Minus size={11} className="text-slate-400" /> : up ? <TrendingUp size={11} className="text-emerald-500" /> : <TrendingDown size={11} className="text-red-400" />}
      <span className={flat ? "" : up ? "text-emerald-600 font-semibold" : "text-red-500 font-semibold"}>
        {flat ? "no change" : `${up ? "+" : "−"}${fmt(d.change)}${d.pct !== null ? ` (${up ? "+" : "−"}${Math.abs(d.pct)}%)` : ""}`}
      </span>
      <span className="text-slate-400">vs prev period{d.pct === null && !flat ? " (new)" : ""}</span>
    </p>
  );
}

export default function AdminOverview() {
  const [period, setPeriod] = useState("daily");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null); // {title, orders, count, total_value}
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(() => {
    const params = { period };
    if (custom.start && custom.end) {
      params.start = custom.start;
      params.end = custom.end;
    }
    api.get("/admin/reports/summary", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [period, custom]);

  useEffect(() => {
    load();
  }, [load]);

  const openDrill = async (title, params) => {
    setDrillLoading(true);
    setDrill({ title, orders: [] });
    try {
      const base = custom.start && custom.end
        ? { start: custom.start, end: custom.end }
        : { period };
      const { data } = await api.get("/admin/reports/orders", { params: { ...base, ...params } });
      setDrill({ title, ...data });
    } finally {
      setDrillLoading(false);
    }
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy">Sales & delivery dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            All figures are live from order, payment and fulfilment records · Africa/Johannesburg time.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="period-selector">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => { setPeriod(p.id); setCustom({ start: "", end: "" }); }}
              className={`text-xs font-bold px-4 py-2 rounded-full transition-colors ${period === p.id && !(custom.start && custom.end) ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-navy/30"}`}
              data-testid={`period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full px-3 py-1.5">
            <input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="text-xs outline-none" data-testid="custom-start" />
            <span className="text-slate-400 text-xs">→</span>
            <input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="text-xs outline-none" data-testid="custom-end" />
          </div>
        </div>
      </div>

      {!data ? (
        <div className="grid sm:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-28 bg-white rounded-2xl border border-slate-100 animate-pulse" />)}</div>
      ) : (
        <>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">
            Sales & orders — click any figure to see the orders behind it
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="sales-metrics">
            {METRICS.map((m) => {
              const d = data.deltas[m.key];
              return (
                <button
                  key={m.key}
                  onClick={() => openDrill(m.label, { metric: m.metric })}
                  className={`text-left rounded-2xl border p-4 transition-colors hover:border-gold ${m.hero ? "bg-navy text-white border-navy" : "bg-white border-slate-200/80"}`}
                  data-testid={`metric-${m.key}`}
                >
                  <p className={`text-[10px] font-bold uppercase tracking-wider ${m.hero ? "text-gold" : "text-slate-500"}`}>{m.label}</p>
                  <p className={`font-display font-extrabold text-xl mt-1.5 font-mono ${m.hero ? "text-gold" : "text-navy"}`}>
                    {m.money ? formatR(d.current) : d.current}
                  </p>
                  <Delta d={d} money={m.money} />
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-3" data-testid="definitions-note">
            Gross value = all orders · Paid sales = verified PayFast/demo payments · Net sales = paid minus refunds & unreconciled cancellations · Pending payments never count as revenue.
          </p>

          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-10 mb-3">
            Fulfilment & delivery monitoring — all open & completed orders
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="fulfilment-buckets">
            {data.fulfilment.map((f) => (
              <button
                key={f.bucket}
                onClick={() => openDrill(f.label, { bucket: f.bucket })}
                className="text-left bg-white rounded-2xl border border-slate-200/80 p-4 hover:border-gold transition-colors"
                data-testid={`bucket-${f.bucket}`}
              >
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{f.label}</p>
                <p className="font-display font-extrabold text-xl mt-1.5 text-navy">{f.count}</p>
                <p className="text-[11px] text-slate-400 font-mono mt-0.5">{formatR(f.value)}</p>
              </button>
            ))}
          </div>
        </>
      )}

      {drill && (
        <div className="mt-8 bg-white rounded-3xl border border-gold/40 p-6" data-testid="drill-down">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-bold text-navy">
              {drill.title} <span className="text-xs font-mono text-slate-400 ml-2">{drill.count ?? "…"} orders · {formatR(drill.total_value || 0)}</span>
            </h3>
            <button onClick={() => setDrill(null)} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="drill-close" aria-label="Close">
              <X size={16} />
            </button>
          </div>
          {drillLoading ? (
            <div className="h-24 bg-slate-50 rounded-xl animate-pulse" />
          ) : drill.orders.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="drill-empty">No orders in this category.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]" data-testid="drill-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="px-3 py-2">Order</th>
                    <th className="px-3 py-2">Customer</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Method</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Payment</th>
                    <th className="px-3 py-2">Delivery / collection</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {drill.orders.map((o) => (
                    <tr key={o.id} className="border-b border-slate-50" data-testid={`drill-order-${o.order_number}`}>
                      <td className="px-3 py-2.5 font-mono font-bold text-navy text-xs">{o.order_number}</td>
                      <td className="px-3 py-2.5">{o.customer.name}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{new Date(o.created_at).toLocaleString("en-ZA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-3 py-2.5 text-xs">{o.delivery_method}</td>
                      <td className="px-3 py-2.5"><span className="text-[10px] font-bold bg-cream px-2 py-0.5 rounded-full text-navy">{ORDER_STATUS_LABELS[o.status] || o.status}</span></td>
                      <td className="px-3 py-2.5"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${o.payment_status === "paid" ? "bg-emerald-50 text-emerald-600" : "bg-gold/15 text-gold-hover"}`}>{o.payment_status}</span></td>
                      <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[220px] truncate">
                        {o.delivery_method === "delivery" && o.address ? `${o.address.line1}, ${o.address.city}` : "In-store collection"}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-navy">{formatR(o.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
